import { useSyncExternalStore } from 'react'

export type StationPageTheme = 'light' | 'dark'

const DARK_QUERY = '(prefers-color-scheme: dark)'

/**
 * Resolve the theme the station-page CSS is rendering with: the
 * `html[data-theme]` attribute wins when present (the Night Tube writer
 * / forced override — same precedence as the `--sp-*` token blocks in
 * stationPageTheme.css), otherwise the OS `prefers-color-scheme`
 * preference. JS consumers (line hexes via `lineHexForTheme`, the
 * map-preview WebGL clear color and geometry set) MUST use this signal
 * so painted colors flip together with the CSS tokens.
 *
 * The station page mounts OUTSIDE AppShell's `MetroMapStoreProvider`,
 * so `useEffectiveTheme` (the map surfaces' signal) is unavailable
 * here — this hook watches the same two inputs directly.
 */
function resolveStationPageTheme(): StationPageTheme {
  const forced = document.documentElement.dataset.theme
  if (forced === 'dark' || forced === 'light') return forced
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function subscribe(onChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY)
  media.addEventListener('change', onChange)
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => {
    media.removeEventListener('change', onChange)
    observer.disconnect()
  }
}

/** Subscription-backed {@link resolveStationPageTheme}. */
export function useStationPageTheme(): StationPageTheme {
  return useSyncExternalStore(subscribe, resolveStationPageTheme)
}
